import { forwardRef, useId, type SelectHTMLAttributes } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  helperText?: string;
  /** `disabled` : option visible mais non sélectionnable (module « bientôt »…). */
  options: { value: string; label: string; disabled?: boolean }[];
  placeholder?: string;
  /** Astérisque + `aria-required` (voir Input). */
  required?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, label, error, helperText, options, placeholder, id, required, ...props }, ref) => {
    // useId garantit un id unique même quand deux champs partagent un label.
    const autoId = useId();
    const selectId = id ?? autoId;
    const errorId = `${selectId}-error`;
    const helpId = `${selectId}-help`;

    return (
      <div className="space-y-1.5">
        {label && (
          <label
            htmlFor={selectId}
            className="block text-[13px] font-medium text-text"
          >
            {label}
            {required && <span aria-hidden="true" className="ml-0.5 text-danger">*</span>}
          </label>
        )}
        {/* Le chevron est une icône (currentColor) et non une data-URL : la
            couleur suit les tokens et reste visible en thème sombre. */}
        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            aria-invalid={error ? true : undefined}
            aria-required={required || undefined}
            aria-describedby={error ? errorId : helperText ? helpId : undefined}
            className={cn(
              'block w-full rounded border border-border bg-surface px-3.5 py-2.5 text-sm text-text transition-all duration-150',
              'focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent',
              'appearance-none pr-10',
              error && 'border-danger focus:border-danger focus:ring-danger',
              'disabled:cursor-not-allowed disabled:bg-surface-subtle disabled:text-subtle',
              className,
            )}
            {...props}
          >
            {placeholder && (
              <option value="">{placeholder}</option>
            )}
            {options.map((opt) => (
              <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                {opt.label}
              </option>
            ))}
          </select>
          <ChevronDown
            className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-subtle"
            aria-hidden="true"
          />
        </div>
        {error && <p id={errorId} className="text-xs text-danger">{error}</p>}
        {helperText && !error && (
          <p id={helpId} className="text-xs text-subtle">{helperText}</p>
        )}
      </div>
    );
  },
);

Select.displayName = 'Select';
